import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PlacementAnalyticsService from './PlacementAnalyticsService';
import { directoryByCompanyPath, isDirectoryLinkEnabled, type PlacementRoleBase } from './deepLinks';
import './placementAnalytics.css';

interface Props {
    instituteId: number | string;
    roleBase: PlacementRoleBase;
}

const LEAK_META: {
    key: string;
    leakType: string;
    short: string;
    title: string;
    blurb: string;
    accent: string;
}[] = [
    {
        key: 'conversionGap',
        leakType: 'CONVERSION_GAP',
        short: 'Leak 1',
        title: 'Conversion Gap',
        blurb: 'Untapped + lapsed companies',
        accent: '#e4572e',
    },
    {
        key: 'scaleGap',
        leakType: 'SCALE_GAP',
        short: 'Leak 2',
        title: 'Scale Gap',
        blurb: 'Underhiring — low offer volumes',
        accent: '#f0a202',
    },
    {
        key: 'salaryGap',
        leakType: 'SALARY_GAP',
        short: 'Leak 3',
        title: 'Quality Gap',
        blurb: 'Below quality / role benchmark',
        accent: '#2e86ab',
    },
];

const LEAK_LABEL: Record<string, string> = {
    CONVERSION_GAP: 'Leak 1 · Conversion Gap',
    SCALE_GAP: 'Leak 2 · Scale Gap',
    SALARY_GAP: 'Leak 3 · Quality Gap',
};

function initialOf(name?: string) {
    return String(name || '?').trim().charAt(0).toUpperCase() || '?';
}

function SeniorityBar({ row }: { row: any }) {
    const tiers = [
        { key: 'Founders', n: Number(row.foundersCount) || 0, color: '#5b8def' },
        { key: 'Exec Leadership', n: Number(row.execLeadershipCount) || 0, color: '#7e57c2' },
        { key: 'Senior Mgmt', n: Number(row.seniorMgmtCount) || 0, color: '#26a69a' },
        { key: 'Mid Mgmt', n: Number(row.midMgmtCount) || 0, color: '#ffa726' },
        { key: 'Mgr & Others', n: Number(row.managerOthersCount) || 0, color: '#ef5350' },
    ];
    const total = tiers.reduce((s, t) => s + t.n, 0);
    if (!total) return null;
    return (
        <div className="pf-seniority">
            <div className="pf-seniority-bar">
                {tiers
                    .filter((t) => t.n > 0)
                    .map((t) => (
                        <span
                            key={t.key}
                            style={{ width: `${(t.n / total) * 100}%`, background: t.color }}
                            title={`${t.key}: ${t.n}`}
                        />
                    ))}
            </div>
            <div className="pf-seniority-legend">
                {tiers.map((t) => (
                    <span key={t.key}>
                        <i style={{ background: t.color }} />
                        {t.key} {t.n}
                    </span>
                ))}
            </div>
        </div>
    );
}

function metaLine(row: any, leakType: string) {
    const alumni = row.totalAlumniCount ?? row.alumniRowCount;
    const bits: ReactNode[] = [];
    if (alumni != null) bits.push(`${alumni} alumni`);
    if (leakType === 'CONVERSION_GAP') {
        if (row.influenceCategory) {
            const cat = String(row.influenceCategory).toLowerCase();
            const label = cat.charAt(0).toUpperCase() + cat.slice(1);
            bits.push(label);
        }
        // bits.push(
        //     <span key="never-visited" className="pf-never-visited">
        //         Never visited
        //     </span>
        // );
    } else if (leakType === 'SCALE_GAP') {
        if (row.offers != null) bits.push(`Offers: ${row.offers}`);
        if (row.last3yr != null) bits.push(row.last3yr ? 'Came last 3yr' : 'No last 3yr');
    } else if (leakType === 'SALARY_GAP') {
        if (row.gapLpa != null) bits.push(`Gap ₹${row.gapLpa}L`);
        if (row.ctcLpa != null && row.benchmarkLpa != null) {
            bits.push(`₹${row.ctcLpa}L vs ₹${row.benchmarkLpa}L`);
        }
    }
    return bits.reduce<ReactNode[]>((acc, bit, i) => {
        if (i > 0) acc.push(' · ');
        acc.push(bit);
        return acc;
    }, []);
}

const PlacementActionables = ({ instituteId, roleBase }: Props) => {
    const [data, setData] = useState<any>(null);
    const [focus, setFocus] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pickQ, setPickQ] = useState('');
    const [pickResults, setPickResults] = useState<any[]>([]);
    const [openLeaks, setOpenLeaks] = useState<Record<string, boolean>>({
        conversionGap: true,
        scaleGap: true,
        salaryGap: true,
    });
    const [confirm, setConfirm] = useState<{
        companyId: number;
        companyName: string;
        action: 'ADD' | 'REMOVE';
        leakType?: string;
    } | null>(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        const [a, f] = await Promise.all([
            PlacementAnalyticsService.getActionables(instituteId),
            PlacementAnalyticsService.getFocusList(instituteId),
        ]);
        setData(a.data);
        setFocus(f.data);
    }, [instituteId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        refresh()
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load actionables');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [refresh]);

    useEffect(() => {
        if (!pickQ.trim()) {
            setPickResults([]);
            return;
        }
        const t = setTimeout(() => {
            PlacementAnalyticsService.companySearch(instituteId, pickQ.trim())
                .then((res) => setPickResults(res.data || []))
                .catch(() => setPickResults([]));
        }, 300);
        return () => clearTimeout(t);
    }, [pickQ, instituteId]);

    const runFocus = async () => {
        if (!confirm) return;
        setBusy(true);
        setError(null);
        try {
            await PlacementAnalyticsService.focusCompany(instituteId, {
                companyId: confirm.companyId,
                action: confirm.action,
                leakType: confirm.leakType,
            });
            setConfirm(null);
            await refresh();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Focus update failed');
        } finally {
            setBusy(false);
        }
    };

    const activeItems = useMemo(
        () => (focus?.items || []).filter((i: any) => i.status === 'ACTIVE'),
        [focus]
    );
    const activeIds = useMemo(
        () => new Set(activeItems.map((i: any) => Number(i.companyId))),
        [activeItems]
    );

    if (loading) return <div className="text-muted font-13">Loading actionables…</div>;

    const stats = data?.quickStats || {};
    const sections = data?.sections || {};
    const active = focus?.activeCount ?? activeItems.length;
    const cap = focus?.cap ?? 20;
    const q = search.trim().toLowerCase();

    return (
        <div className="pf-page">
            {error && <div className="alert alert-danger py-2 font-13">{error}</div>}

            <div className="mb-2">
                <h3 className="placement-section-title mb-1">Placement Focus</h3>
                <p className="pf-subtitle">
                    Select companies to prioritise this placement season based on the 3 Leaks analysis.
                </p>
            </div>

            <div className="pf-stat-row">
                <div className="pf-stat-card" style={{ borderLeftColor: '#123d66' }}>
                    <div className="pf-stat-label">Companies in Focus</div>
                    <div className="pf-stat-value">
                        {active} <span>/ {cap}</span>
                    </div>
                    <div className="pf-stat-blurb">selected for current season</div>
                </div>
                {LEAK_META.map((m) => (
                    <div key={m.key} className="pf-stat-card" style={{ borderLeftColor: m.accent }}>
                        <div className="pf-stat-label">
                            {m.short} · {m.title}
                        </div>
                        <div className="pf-stat-value" style={{ color: m.accent }}>
                            {stats[m.key] ?? sections[m.key]?.length ?? 0}
                        </div>
                        <div className="pf-stat-blurb">{m.blurb}</div>
                    </div>
                ))}
            </div>

            <div className="pf-layout">
                <div className="pf-main">
                    <div className="pf-search">
                        <span className="pf-search-icon" aria-hidden="true">
                            ⌕
                        </span>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search companies where alumni work…"
                        />
                    </div>

                    {!data?.uploadId && (
                        <div className="alert alert-info py-2 font-13">
                            No ACTIONABLES upload yet — you can still manage focus from Univariety picks.
                        </div>
                    )}

                    {LEAK_META.map((m) => {
                        const allRows = sections[m.key] || [];
                        if (!allRows.length) return null;
                        const rows = q
                            ? allRows.filter((r: any) =>
                                  String(r.companyName || '')
                                      .toLowerCase()
                                      .includes(q)
                              )
                            : allRows;
                        const added = allRows.filter((r: any) => activeIds.has(Number(r.companyId))).length;
                        const open = openLeaks[m.key] !== false;
                        return (
                            <div key={m.key} className="pf-leak-block">
                                <button
                                    type="button"
                                    className="pf-leak-header"
                                    onClick={() =>
                                        setOpenLeaks((s) => ({ ...s, [m.key]: !open }))
                                    }>
                                    <span className="pf-leak-pill" style={{ background: m.accent }}>
                                        {m.short}
                                    </span>
                                    <span className="pf-leak-title">{m.title}</span>
                                    <span className="pf-leak-meta">
                                        {allRows.length} companies · <strong>{added} added</strong>
                                    </span>
                                    <span className="pf-leak-chevron">{open ? '▾' : '▸'}</span>
                                </button>
                                {open && (
                                    <div className="pf-company-list">
                                        {rows.map((r: any) => {
                                            const inFocus = activeIds.has(Number(r.companyId));
                                            return (
                                                <div key={`${m.leakType}-${r.companyId}`} className="pf-company-card">
                                                    <div
                                                        className="pf-avatar"
                                                        style={{ background: m.accent }}>
                                                        {initialOf(r.companyName)}
                                                    </div>
                                                    <div className="pf-company-body">
                                                        <div className="pf-company-top">
                                                            {isDirectoryLinkEnabled(roleBase) ? (
                                                                <Link
                                                                    to={directoryByCompanyPath(r.companyId)}
                                                                    className="pf-company-name">
                                                                    {r.companyName}
                                                                </Link>
                                                            ) : (
                                                                <span className="pf-company-name">{r.companyName}</span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className={`pf-add-btn ${inFocus ? 'added' : ''}`}
                                                                onClick={() =>
                                                                    setConfirm({
                                                                        companyId: Number(r.companyId),
                                                                        companyName: r.companyName,
                                                                        action: inFocus ? 'REMOVE' : 'ADD',
                                                                        leakType: m.leakType,
                                                                    })
                                                                }>
                                                                {inFocus ? '✓ Added' : '+ Add'}
                                                            </button>
                                                        </div>
                                                        <div className="pf-company-meta">
                                                            {metaLine(r, m.leakType)}
                                                        </div>
                                                        <SeniorityBar row={r} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {!rows.length && (
                                            <div className="text-muted font-13 p-3">No matches</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <aside className="pf-sidebar">
                    <div className="pf-sidebar-head">
                        <div>
                            <div className="pf-sidebar-title">Focus List</div>
                            <div className="pf-sidebar-sub">
                                {active} of {cap} selected
                            </div>
                        </div>
                        <div className="pf-sidebar-actions">
                            <button
                                type="button"
                                className="pf-btn-export"
                                onClick={() => PlacementAnalyticsService.exportFocusList(instituteId)}>
                                Export
                            </button>
                        </div>
                    </div>

                    <div className="pf-pick">
                        <label>Add Univariety pick</label>
                        <input
                            value={pickQ}
                            onChange={(e) => setPickQ(e.target.value)}
                            placeholder="Search institute companies…"
                        />
                        {pickResults.length > 0 && (
                            <div className="pf-pick-results">
                                {pickResults.map((c) => (
                                    <button
                                        key={c.companyId}
                                        type="button"
                                        onClick={() => {
                                            setConfirm({
                                                companyId: Number(c.companyId),
                                                companyName: c.companyName,
                                                action: 'ADD',
                                            });
                                            setPickQ('');
                                            setPickResults([]);
                                        }}>
                                        {c.companyName}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="pf-focus-list">
                        {activeItems.map((item: any, idx: number) => (
                            <div key={item.id} className="pf-focus-item">
                                <div className="pf-focus-num">{idx + 1}</div>
                                <div className="pf-focus-body">
                                    <div className="pf-focus-name-row">
                                        {isDirectoryLinkEnabled(roleBase) ? (
                                            <Link to={directoryByCompanyPath(item.companyId)} className="pf-company-name">
                                                {item.companyName}
                                            </Link>
                                        ) : (
                                            <span className="pf-company-name">{item.companyName}</span>
                                        )}
                                        <button
                                            type="button"
                                            className="pf-remove"
                                            title="Remove"
                                            onClick={() =>
                                                setConfirm({
                                                    companyId: Number(item.companyId),
                                                    companyName: item.companyName,
                                                    action: 'REMOVE',
                                                    leakType: item.leakType,
                                                })
                                            }>
                                            ×
                                        </button>
                                    </div>
                                    <div className="pf-focus-tags">
                                        <span className="pf-tag">
                                            {item.leakType
                                                ? LEAK_LABEL[item.leakType] || item.leakType
                                                : 'Univariety pick'}
                                        </span>
                                    </div>
                                    {isDirectoryLinkEnabled(roleBase) && (
                                        <Link
                                            to={directoryByCompanyPath(item.companyId)}
                                            className="pf-watchlist-link">
                                            View Alumni in Directory →
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                        {!activeItems.length && (
                            <div className="text-muted font-13 p-3 text-center">No companies in focus yet</div>
                        )}
                    </div>
                </aside>
            </div>

            {confirm && (
                <div className="pf-modal-backdrop">
                    <div className="pf-modal">
                        <h4>{confirm.action === 'ADD' ? 'Add to Focus?' : 'Remove from Focus?'}</h4>
                        <p>{confirm.companyName}</p>
                        <div className="pf-modal-actions">
                            <button type="button" className="pf-btn-export" disabled={busy} onClick={() => setConfirm(null)}>
                                Cancel
                            </button>
                            <button type="button" className="pf-btn-primary" disabled={busy} onClick={runFocus}>
                                {busy ? 'Saving…' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlacementActionables;
